<template>
  <v-card>
    <v-card-title v-if="options.title">
      <span class="headline">{{ options.title }} - {{ $t('PRINT_BY_BOOK') }}</span>
    </v-card-title>
    <v-divider></v-divider>
    <v-card-title>
      <v-row class="mb-4" no-gutters>
        <v-col cols="12">
          <v-alert color="error">
            <div class="white--text">
              Stampando i bollettini verrà intaccata la giacenza
            </div>
            <div class="white--text font-weight-bold">
              {{ $t('PRINT_LIST') }}: {{ cartTeacherIds.length }}/50
            </div>
          </v-alert>
        </v-col>
      </v-row>
    </v-card-title>
    <v-card-text>
      <ValidationObserver
        ref="form"
        tag="form"
        @submit.prevent="save"
      >
        <ValidationProvider
          rules="required"
          vid="propagandist"
          :name="$t('fields.propagandist').toLowerCase()"
          v-slot="{ errors }"
        >
          <PropagandistSelect
            :errors="errors"
            v-model="form.propagandist_id"
          >
          </PropagandistSelect>
        </ValidationProvider>

        <ValidationProvider
          rules="required"
          vid="name"
          :name="$t('fields.note').toLowerCase()"
          v-slot="{ errors }"
        >
          <v-text-field
            v-model="form.name"
            :counter="255"
            :label="$t('fields.note')"
            :error-messages="errors"
          ></v-text-field>
        </ValidationProvider>
      </ValidationObserver>

      <v-container fill-height fluid>
        <v-row>
          <v-flex xs6 class="mr-2">
            <Search
              v-model="filters.isbn"
              :label="$t('SEARCH_BOOK')"
              prepend-icon="search"
              :delay="500"
            />
          </v-flex>
          <v-flex xs3 class="mr-2">
            <v-select
              :items="years"
              multiple
              v-model="filters.years"
              attach
              chips
              :label="$t('fields.years')"
            >
            </v-select>
          </v-flex>
        </v-row>
      </v-container>

      <v-data-table
        item-key="campaign_book.id"
        :headers="headers"
        :items="items"
        :server-items-length="totalItems"
        :no-data-text="$t('NO_DATA')"
        :no-results-text="$t('NO_DATA')"
        hide-default-footer
        :options.sync="pagination"
        :loading="loading"
        class="elevation-1"
      >
        <template v-slot:[`item.checkbox`]="{ item }">
          <v-chip v-if="isInCart(item.campaign_book.id)" small color="success" dark>
            {{ $t('PRINT_LIST') }}
          </v-chip>
          <v-tooltip v-else-if="!item.campaign_book.can_print" bottom>
            <template v-slot:activator="{ on, attrs }">
              <span v-bind="attrs" v-on="on">
                <v-checkbox :input-value="false" disabled></v-checkbox>
              </span>
            </template>
            <span>{{ $t('TEACHER_OTHER_PROPAGANDIST_SCHOOL') }}</span>
          </v-tooltip>
          <v-checkbox
            v-else
            v-model="bookSelected"
            :value="item.campaign_book.id"
            :disabled="getProductAvailability(item.book.isbn) <= 0 && !bookSelected.includes(item.campaign_book.id)"
          ></v-checkbox>
        </template>

        <template v-slot:[`item.status`]="{ item }">
          {{ $t(`campaign_statuses.${getLastStatus(item.campaign_book.statuses).status}`) }}
        </template>
        <template v-slot:[`item.updated_at`]="{ item }">
          {{ getLastStatus(item.campaign_book.statuses).updated_at | timeFormat }}
        </template>
        <template v-slot:[`item.teachers.surname`]="{ item }">
          {{ item.teacher.fullname }}
        </template>
        <template v-slot:[`item.school`]="{ item }">
          <span v-if="item.schools.location_options && item.schools.location_options.branch_code">
            {{ item.schools.location_options.branch_code }}
          </span>
          {{ item.schools.name }}
        </template>
        <template v-slot:[`item.title`]="{ item }">
          {{ item.book.title }}
        </template>
        <template v-slot:[`item.isbn`]="{ item }">
          {{ item.book.isbn }}
        </template>
        <template v-slot:[`item.volume`]="{ item }">
          {{ item.book.volume }}
        </template>
        <template v-slot:[`item.qty`]="{ item }">
          <v-badge
            v-if="getProductAvailability(item.book.isbn)"
            color="error"
            :content="getProductAvailability(item.book.isbn)"
            inline
          ></v-badge>
          <span v-else>0</span>
        </template>
        <template v-slot:[`item.book_type`]="{ item }">
          {{ item.book.book_type }}
        </template>
        <template v-slot:[`item.editor`]="{ item }">
          {{ item.editor.name }}
        </template>
        <template v-slot:[`item.created_at`]="{ item }">
          {{ item.campaign_book.created_at | timeFormat }}
        </template>
      </v-data-table>
      <div class="text-xs-center pt-2">
        <Paginator
          :total-items="totalItems"
          :page.sync="pagination.page"
          :items-per-page.sync="pagination.itemsPerPage"
          @change="fetchData"
        />
      </div>

      <v-row class="mt-2">
        <v-col cols="12">
          <v-btn
            color="primary"
            :disabled="!bookSelected.length"
            @click="addToCart"
          >
            {{ $t('ADD_TO_PRINT_LIST') }} ({{ bookSelected.length }})
          </v-btn>
        </v-col>
      </v-row>

      <v-card class="mt-4" outlined>
        <v-card-title class="subtitle-1">
          {{ $t('PRINT_LIST') }} ({{ cart.length }})
        </v-card-title>
        <v-card-text>
          <div v-if="!cart.length">{{ $t('PRINT_LIST_EMPTY') }}</div>
          <v-simple-table v-else dense>
            <template v-slot:default>
              <thead>
                <tr>
                  <th class="text-left">{{ $t('fields.teacher') }}</th>
                  <th class="text-left">{{ $t('fields.isbn') }}</th>
                  <th class="text-left">{{ $t('fields.title') }}</th>
                  <th class="text-left">{{ $t('fields.volume') }}</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(cartItem, index) in cart" :key="index">
                  <td>{{ cartItem.teacher }}</td>
                  <td>{{ cartItem.isbn }}</td>
                  <td>{{ cartItem.title }}</td>
                  <td>{{ cartItem.volume }}</td>
                  <td>
                    <v-btn icon color="red" @click="removeFromCart(cartItem.campaignBookId)">
                      <v-icon>cancel</v-icon>
                    </v-btn>
                  </td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>
        </v-card-text>
      </v-card>
    </v-card-text>
    <v-card-actions>
      <v-btn :disabled="isNotReadyToSave" @click="save" color="primary">
        {{ $t('PRINT') }} ({{ cart.length }})
      </v-btn>
      <v-btn @click="cancel">{{ $t('CANCEL') }}</v-btn>
    </v-card-actions>
  </v-card>
</template>
<script>
  import { mapActions, mapGetters, mapState } from 'vuex';

  import { campaignsService } from '../../../utils/services/campaigns.service';
  import { warehousesService } from '../../../utils/services/warehouses.service';

  import PropagandistSelect from '../../shared/Vselects/PropagandistSelect.vue';
  import Search from '../../shared/Inputs/Search.vue';
  import Paginator from '../../shared/Paginator';

  export default {
    name: 'BullettinByBookModal',
    components: {
      PropagandistSelect,
      Search,
      Paginator,
    },
    data() {
      return {
        stored: null,
        loading: false,
        items: [],
        itemsObject: {},
        products: {},
        bookSelected: [],
        cart: [],
        totalItems: 0,
        years: [],
        pagination: {
          page: 1,
          itemsPerPage: 10,
          sortDesc: [false],
          sortBy: ['created_at'],
        },
        form: {
          name: '',
          shipping_type: 'ALL',
          propagandist_id: null,
          teacherIds: [],
          schoolIds: [],
          campaignBookIds: [],
        },
        filters: {
          years: null,
          isbn: null,
          statuses: ['ASSIGNED', 'STATUS_BOOKED'],
          shippingType: ['SCHOOL', 'HOME'],
        },
        headers: [
          { text: '', value: 'checkbox', sortable: false },
          { text: this.$t('fields.teacher'), value: 'teachers.surname' },
          { text: this.$t('fields.school'), value: 'school', sortable: false },
          { text: this.$t('fields.isbn'), value: 'isbn' },
          { text: this.$t('fields.title'), value: 'title' },
          { text: this.$t('fields.volume'), value: 'volume' },
          { text: this.$t('fields.editor'), value: 'editor' },
          { text: this.$t('fields.type_shipping'), value: 'shipping_type' },
          { text: this.$t('fields.book_type'), value: 'book_type' },
          { text: this.$t('fields.created_at'), value: 'created_at' },
          { text: this.$t('fields.status'), value: 'status', sortable: false },
          { text: this.$t('fields.updated_at'), value: 'updated_at' },
          { text: this.$t('fields.quantity'), value: 'qty' },
        ],
        errors: {},
      };
    },
    computed: {
      ...mapState('modal', ['options', 'data']),
      ...mapGetters('user', ['currentUser']),

      cartCampaignBookIds() {
        return this.cart.map(x => x.campaignBookId);
      },

      cartTeacherIds() {
        return [...new Set(this.cart.map(x => x.teacherId))];
      },

      getProducts() {
        return this.products;
      },

      isNotReadyToSave() {
        return !this.cart.length;
      },

      requestFilters() {
        const filters = {
          years: this.filters.years,
          isbn: this.filters.isbn,
          statuses: this.filters.statuses,
          shipping_type: this.filters.shippingType,
        };

        if (this.pagination.sortBy.length === 1 && this.pagination.sortDesc.length === 1) {
          const { sortBy, sortDesc } = this.pagination;
          filters.sortBy = sortBy;
          filters.sortDesc = sortDesc;
        }
        return filters;
      },

      getSkuVisibles() {
        return this.items.map(x => x.book.isbn);
      },
    },
    methods: {
      ...mapActions('alert', ['showToast']),
      ...mapActions('modal', ['hideModal', 'loadingModal']),

      isInCart(campaignBookId) {
        return this.cart.some(x => x.campaignBookId === campaignBookId);
      },

      // Disponibilità residua: giacenza meno quanto già nel carrello e quanto
      // spuntato ora, per lo stesso isbn, così non si supera lo stock.
      getProductAvailability(isbn) {
        const inCart = this.cart.filter(x => x.isbn === isbn).length;
        const inCurrent = this.bookSelected.filter(
          id => this.itemsObject[id] && this.itemsObject[id].book.isbn === isbn,
        ).length;
        return (this.getProducts[isbn] || 0) - inCart - inCurrent;
      },

      addToCart() {
        let skipped = 0;
        this.bookSelected.forEach((campaignBookId) => {
          if (this.isInCart(campaignBookId)) {
            return;
          }
          const item = this.itemsObject[campaignBookId];
          if (!item) {
            return;
          }
          const { isbn } = item.book;
          // Tetto rigido: non superare la giacenza (copie già in lista per lo stesso isbn).
          const inCart = this.cart.filter(x => x.isbn === isbn).length;
          const available = (this.products[isbn] || 0) - inCart;
          if (available <= 0) {
            skipped += 1;
            return;
          }
          this.cart.push({
            campaignBookId,
            teacherId: item.teacher.id,
            teacher: item.teacher.fullname,
            isbn,
            title: item.book.title,
            volume: item.book.volume,
          });
        });
        this.bookSelected = [];
        if (skipped > 0) {
          this.showToast({ type: 'error', message: this.$t('INSUFFICIENT_STOCK') });
        } else {
          this.showToast({ type: 'success', message: this.$t('ADD_TO_PRINT_LIST') });
        }
      },

      removeFromCart(campaignBookId) {
        this.cart = this.cart.filter(x => x.campaignBookId !== campaignBookId);
      },

      initYears() {
        this.years = [];
        const year = new Date().getFullYear();
        for (let i = 0; i <= 5; i += 1) {
          this.years.push(year - i);
        }
      },

      getLastStatus(items) {
        return items[items.length - 1];
      },

      async fetchProductQuantity() {
        return warehousesService.productQuantityAvailableInWarehouse(
          this.currentUser.user.warehouses[0], {
            filters: {
              skuIn: this.getSkuVisibles,
            },
          },
        );
      },

      async fetchCampaigns(forcePage) {
        const { page, itemsPerPage } = this.pagination;
        return campaignsService.teacherIndex({
          page: forcePage || page || 1,
          itemsPerPage,
          filters: this.requestFilters,
        });
      },

      async fetchData(page) {
        this.loading = true;
        return this.fetchCampaigns(page).then((campaignresponse) => {
          this.items = campaignresponse.data.data;
          this.itemsObject = {};
          campaignresponse.data.data.forEach((item) => {
            this.itemsObject[item.campaign_book.id] = item;
          });

          this.totalItems = campaignresponse.data.meta.total;
          this.pagination = this.getPagination(
            campaignresponse.data.meta,
            { oldPagination: this.pagination, page },
          );
          if (!this.getSkuVisibles.length) {
            return null;
          }
          return this.fetchProductQuantity();
        }).then((productresponse) => {
          if (productresponse) {
            // Riassegno l'oggetto (non muto le chiavi) così la reattività Vue 2
            // aggiorna la disponibilità mostrata quando cambia il carrello.
            const products = { ...this.products };
            Object.keys(productresponse.data.data).forEach((key) => {
              const { isbn, qty } = productresponse.data.data[key];
              products[isbn] = qty;
            });
            this.products = products;
          }
        })
          .catch((error) => {
            this.handleError(error, this.errors);
          })
          .finally(() => {
            this.loading = false;
          });
      },

      async save() {
        const isValid = await this.$refs.form.validate();
        if (isValid && this.cart.length) {
          if (this.options.onSave) {
            this.loadingModal(true);
            this.form.teacherIds = this.cartTeacherIds;
            this.form.campaignBookIds = this.cartCampaignBookIds;
            this.options.onSave(
              this.form,
              this.$refs.form,
            )
              .then(() => {
                this.loadingModal(false);
              })
              .catch((error) => {
                this.handleError(error, this.errors);
              });
          }
        }
      },
      cancel() {
        this.hideModal({});
      },
    },
    async created() {
      this.initYears();
      await this.fetchData(1);
    },
    watch: {
      filters: {
        deep: true,
        handler() {
          this.fetchData(1);
        },
      },
      'pagination.sortBy': {
        handler(oldVal, newVal) {
          if (newVal !== oldVal) {
            this.fetchData(1);
          }
        },
      },
    },
  };
</script>
